import React, {useCallback, useEffect, useState} from 'react';
import {DeclutterDuel, DuelSide, fetchNextDuel, generateDuels, resolveDuel} from '../service/DeclutterApi';
import Spinner from './Spinner';
import AuthImage from './AuthImage';

/**
 * Дуэль дублей: «оставить только одну».
 *
 * Формат существует, потому что выбрать лучшую из пары человеку заметно легче, чем судить
 * одинокую вещь. Поэтому основные кнопки — именно выбор одной из двух, а «сохранить обе» и
 * «пропустить» намеренно оформлены слабее: с равновесными кнопками пользователь будет
 * систематически уходить от решения.
 */
function SideCard({side, recommended, onKeep, disabled}: {
    side: DuelSide | null;
    recommended: boolean;
    onKeep: () => void;
    disabled: boolean;
}) {
    if (!side) return null;
    return (
        <div className={`card p-4 ${recommended ? 'ring-2 ring-brand-500' : ''}`}
             data-testid={`duel-side-${side.itemId}`}>
            {recommended && (
                <span className="badge badge-success mb-2 inline-block">Рекомендация AI</span>
            )}
            <div className="flex gap-3">
                {side.primaryPhotoUrl ? (
                    <AuthImage imageKey={side.primaryPhotoUrl} alt={side.name ?? ''} variant="thumbnail"
                               className="w-24 h-32 object-cover rounded-xl"/>
                ) : (
                    <div className="w-24 h-32 rounded-xl bg-gray-100 dark:bg-ink-700"/>
                )}
                <dl className="text-sm text-gray-700 dark:text-gray-300 min-w-0">
                    <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                        {side.name}
                    </div>
                    {side.brandName && <div>{side.brandName}</div>}
                    {side.conditionName && <div>Состояние: {side.conditionName}</div>}
                    <div>Носок: {side.totalWears ?? 0}</div>
                    {side.score !== null && <div>Оценка: {side.score} из 100</div>}
                </dl>
            </div>
            <button type="button" disabled={disabled} onClick={onKeep}
                    data-testid={`duel-keep-${side.itemId}`}
                    className={`mt-3 w-full min-h-[44px] rounded-xl text-sm font-medium
                        ${recommended ? 'btn-accent' : 'btn-secondary'}`}>
                Оставить эту
            </button>
        </div>
    );
}

export default function DeclutterDuelCard({ownerId, sessionId, onResolved, generate}: {
    ownerId: string | null;
    sessionId: string;
    onResolved?: () => void;
    /** Генерация пар; страница передаёт версию «один раз на сессию» из стора. */
    generate?: (ownerId: string | null, sessionId: string) => Promise<unknown>;
}) {
    const [duel, setDuel] = useState<DeclutterDuel | null>(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            let next = await fetchNextDuel(ownerId, sessionId);
            if (!next) {
                await (generate ?? generateDuels)(ownerId, sessionId);
                next = await fetchNextDuel(ownerId, sessionId);
            }
            setDuel(next);
        } catch {
            setError('Не удалось загрузить сравнение дублей');
        } finally {
            setLoading(false);
        }
    }, [ownerId, sessionId, generate]);

    useEffect(() => {
        void load();
    }, [load]);

    const resolve = async (resolution: string) => {
        if (!duel) return;
        setBusy(true);
        setError(null);
        try {
            await resolveDuel(ownerId, sessionId, duel.id, resolution);
            await load();
            onResolved?.();
        } catch {
            setError('Не удалось сохранить выбор');
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return <div className="flex justify-center py-8"><Spinner/></div>;
    }

    if (!duel) {
        return (
            <div className="card p-6 text-center" data-testid="duel-empty">
                <p className="text-gray-700 dark:text-gray-200">Дублей для сравнения не найдено.</p>
            </div>
        );
    }

    const impact = duel.impact;
    return (
        <div className="space-y-3" data-testid="declutter-duel">
            <div className="card p-4">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Оставить только одну
                </h3>
                {duel.tie && (
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-300" data-testid="duel-tie">
                        Вещи равноценны — выбор за вами.
                    </p>
                )}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
                <SideCard side={duel.itemA} disabled={busy}
                          recommended={duel.winnerItemId === duel.itemA?.itemId}
                          onKeep={() => void resolve('KEEP_A')}/>
                <SideCard side={duel.itemB} disabled={busy}
                          recommended={duel.winnerItemId === duel.itemB?.itemId}
                          onKeep={() => void resolve('KEEP_B')}/>
            </div>

            {duel.criteria.length > 0 && (
                <div className="card p-4 overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                        <tr className="text-left text-gray-500 dark:text-gray-400">
                            <th className="py-1 pr-3">Критерий</th>
                            <th className="py-1 pr-3">{duel.itemA?.name}</th>
                            <th className="py-1 pr-3">{duel.itemB?.name}</th>
                            <th className="py-1">Победитель</th>
                        </tr>
                        </thead>
                        <tbody>
                        {duel.criteria.map(c => (
                            <tr key={c.code} className="border-t border-gray-100 dark:border-ink-700"
                                data-testid="duel-criterion">
                                <td className="py-1 pr-3">{c.label}</td>
                                <td className="py-1 pr-3">{c.valueA ?? '—'}</td>
                                <td className="py-1 pr-3">{c.valueB ?? '—'}</td>
                                <td className="py-1">
                                    {c.winner === 'UNKNOWN' ? 'нет данных'
                                        : c.winner === 'TIE' ? 'поровну'
                                            : c.winner === 'A' ? duel.itemA?.name : duel.itemB?.name}
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}

            {impact && (
                <div className="card p-4" data-testid="duel-impact">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Что изменится
                    </h4>
                    <ul className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                        {impact.compatibilityDeltaPercent !== null && (
                            <li>Сочетаемость гардероба: {impact.compatibilityDeltaPercent > 0 ? '+' : ''}
                                {impact.compatibilityDeltaPercent.toFixed(1)} п. п.</li>
                        )}
                        <li>Пар дублей станет меньше на {impact.duplicatePairsRemoved}</li>
                        <li>Освободится ≈ {Number(impact.freedCubicMeters ?? 0).toFixed(3)} м³</li>
                    </ul>
                    {impact.estimated && (
                        <p className="mt-1 text-xs text-gray-500">
                            Объём и вес — оценка по категории.
                        </p>
                    )}
                </div>
            )}

            <div className="flex flex-wrap gap-2">
                <button type="button" className="btn-secondary min-h-[44px] px-4 rounded-xl"
                        disabled={busy} data-testid="duel-keep-both"
                        onClick={() => void resolve('KEEP_BOTH')}>
                    Сохранить обе
                </button>
                <button type="button" className="btn-secondary min-h-[44px] px-4 rounded-xl"
                        disabled={busy} data-testid="duel-skip"
                        onClick={() => void resolve('SKIP')}>
                    Пропустить пару
                </button>
            </div>
            {error && <p className="text-sm text-red-600 dark:text-red-400" role="status">{error}</p>}
        </div>
    );
}
